import {
  RecoveryFlow,
  Session,
  SettingsFlow,
  UiNode,
  UpdateRecoveryFlowBody,
  UpdateSettingsFlowBody,
  UpdateVerificationFlowBody,
  VerificationFlow,
} from '@ory/client'

import { retryOryAction } from '@web-apps/utils-shared'
import { ory } from './configuration'
import { handleGetFlowError, mapOryFormSubmissionErrorCodes } from './errors'
import { AxiosResponse } from 'axios'

export const getUserAuthSession = async (): Promise<Session | null> => {
  const session = await retryOryAction(() => ory.frontend.toSession())
  return session.data
}

export const initiateLogoutFlow = async (): Promise<{
  url?: string
  token?: string
  error?: {
    message?: string
    status: number
  }
}> => {
  try {
    const { data } = await retryOryAction(() =>
      ory.frontend.createBrowserLogoutFlow()
    )
    return { url: data.logout_url, token: data.logout_token }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      error: {
        message: handleGetFlowError(error, 'initiateLogoutFlow'),
        status: error?.response?.status,
      },
    }
  }
}

export const submitLogoutFlow = async ({
  token,
}: {
  token?: string
}): Promise<null | { error?: string }> => {
  try {
    await retryOryAction(() => ory.frontend.updateLogoutFlow({ token }))
    return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return { error: handleGetFlowError(error, 'submitLogoutFlow') }
  }
}

export const initiateRecoverFlow = async (
  flowId?: string
): Promise<{ flow?: RecoveryFlow; error?: string }> => {
  // If a flow already exists, we proceed to creating a recovery flow
  if (flowId) {
    try {
      const flow: AxiosResponse = await retryOryAction(() =>
        ory.frontend.getRecoveryFlow({ id: flowId })
      )
      return { flow: flow.data }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return {
        error: handleGetFlowError(
          error,
          'initiateRecoverFlow:createBrowserRecoveryFlow'
        ),
      }
    }
  }

  // Otherwise, we initialize the flow manually
  try {
    const flow = await retryOryAction(() =>
      ory.frontend.createBrowserRecoveryFlow()
    )
    return { flow: flow.data }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      error: handleGetFlowError(
        error,
        'initiateRecoverFlow:createBrowserRecoveryFlow'
      ),
    }
  }
}

export const submitRecoverFlow = async (
  flow: RecoveryFlow,
  formValues: UpdateRecoveryFlowBody,
  onLogin: () => void
): Promise<null | { error?: string }> => {
  try {
    await retryOryAction(() =>
      ory.frontend.updateRecoveryFlow({
        flow: flow?.id,
        updateRecoveryFlowBody: formValues,
      })
    )
    onLogin()
    return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const oryUiResponse = error?.response?.data?.ui
    if (oryUiResponse?.nodes) {
      const errorMessage = oryUiResponse.nodes.find(
        (node: UiNode) => node.messages?.length
      )

      if (errorMessage) {
        return {
          error: mapOryFormSubmissionErrorCodes({
            error: errorMessage,
            errorCode: errorMessage.messages[0].id,
            action: 'submitRecoverFlow',
            config: error?.config,
          }),
        }
      }
    }

    return { error: handleGetFlowError(error, 'submitRecoverFlow') }
  }
}

export const initiateSettingsFlow = async (): Promise<{
  flow?: SettingsFlow
  error?: string
}> => {
  try {
    const flow = await retryOryAction(() =>
      ory.frontend.createBrowserSettingsFlow()
    )
    return { flow: flow.data }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return { error: handleGetFlowError(error, 'initiateSettingsFlow') }
  }
}

export const submitSettingsFlow = async (
  flow: SettingsFlow,
  formValues: UpdateSettingsFlowBody
): Promise<null | { error?: string }> => {
  try {
    await retryOryAction(() =>
      ory.frontend.updateSettingsFlow({
        flow: flow?.id,
        updateSettingsFlowBody: formValues,
      })
    )
    return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const oryUiResponse = error?.response?.data?.ui
    if (oryUiResponse?.nodes) {
      const errorMessage = oryUiResponse.nodes.find(
        (node: UiNode) => node.messages?.length
      )

      if (errorMessage) {
        return {
          error: mapOryFormSubmissionErrorCodes({
            error: errorMessage,
            errorCode: errorMessage.messages[0].id,
            action: 'submitSettingsFlow',
            config: error?.config,
          }),
        }
      }
    }

    return { error: handleGetFlowError(error, 'submitSettingsFlow') }
  }
}

export const initiateVerifyEmailFlow = async (
  options?: {
    flowId: string
  } | void
): Promise<{
  flow?: VerificationFlow
  error?: string
}> => {
  let flow: VerificationFlow
  if (options?.flowId) {
    try {
      const response = await retryOryAction(() =>
        ory.frontend.getVerificationFlow({ id: flow.id })
      )
      flow = response.data

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return {
        error: handleGetFlowError(
          error,
          'initiateVerifyEmailFlow:getSelfServiceVerificationFlow'
        ),
      }
    }
  } else {
    try {
      const response = await retryOryAction(() =>
        ory.frontend.createBrowserVerificationFlow()
      )
      flow = response.data

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return {
        error: handleGetFlowError(
          error,
          'initiateVerifyEmailFlow:initializeSelfServiceVerificationFlowForBrowsers'
        ),
      }
    }
  }

  if (flow.ui.messages?.length)
    return {
      error: mapOryFormSubmissionErrorCodes({
        error: flow.ui.messages,
        errorCode: flow.ui.messages[0].id,
        action:
          'initiateVerifyEmailFlow:initializeSelfServiceVerificationFlowForBrowsers',
      }),
    }

  return { flow }
}

export const submitVerifyEmailFlow = async (
  flow: VerificationFlow,
  formValues: UpdateVerificationFlowBody
): Promise<null | { error?: string }> => {
  try {
    await retryOryAction(() =>
      ory.frontend.updateVerificationFlow({
        flow: flow?.id,
        updateVerificationFlowBody: formValues,
      })
    )
    return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const oryUiResponse = error?.response?.data?.ui
    if (oryUiResponse?.nodes) {
      const errorMessage = oryUiResponse.nodes.find(
        (node: UiNode) => node.messages?.length
      )

      if (errorMessage) {
        return {
          error: mapOryFormSubmissionErrorCodes({
            error: errorMessage,
            errorCode: errorMessage.messages[0].id,
            action: 'submitVerifyEmailFlow',
            config: error?.config,
          }),
        }
      }
    }

    return { error: handleGetFlowError(error, 'submitVerifyEmailFlow') }
  }
}
